import type { MarketIconSymbol } from "@kyan/ui";
import {
  Button,
  MarketIcon,
  NumericInput,
  SummaryCollapse,
  Tooltip,
  Typography,
} from "@kyan/ui";

import { AccountSelector } from "#/components/AccountSelector/AccountSelector";
import { tokens } from "#/config/web3/tokens";
import { formatCurrency, formatValue } from "#/utils/valueFormatters";

import { FormInfo } from "../FormInfo";
import { MarketSelector } from "../MarketSelector";
import { Summary } from "../Summary";
import { WalletInfo } from "../WalletInfo";
import { useWithdrawFormController } from "./WithdrawForm.controller";

export const WithdrawForm: React.FC = () => {
  const {
    handleWithdraw,
    ctaLabel,
    tooltip,
    isDisabled,
    isAmountValid,
    isLoading,
    account,
    setAccount,
    amount,
    equityModalPortfolio,
    setEquityModalPortfolio,
    setWithdrawAmount,
    portfolioState,
    usdcBalance,
    errorMessage,
    finalAmount,
    maxWithdrawable,
  } = useWithdrawFormController();

  return (
    <div className="flex w-full flex-col items-center">
      <Typography
        element="h2"
        font="grotesk"
        weight="medium"
        variant="primary"
        className="w-full text-left leading-7"
      >
        Withdraw
      </Typography>

      <div className="flex w-full flex-col">
        <FormInfo
          title="From"
          info={`Total equity ${formatCurrency(portfolioState.equity)}`}
        />

        <AccountSelector account={account} setAccount={setAccount} />

        <MarketSelector
          title="portfolio"
          value={equityModalPortfolio}
          onChange={setEquityModalPortfolio}
        />

        <NumericInput
          value={amount}
          error={errorMessage}
          maxButtonValue={maxWithdrawable}
          leftIcon={<MarketIcon symbol={"usdc" as MarketIconSymbol} />}
          onChange={setWithdrawAmount}
        />

        <FormInfo
          title="To"
          info={`Balance ${formatValue(usdcBalance, { decimals: tokens.USDC.decimals })} USDC`}
        />

        <WalletInfo />

        {account && isAmountValid ? (
          <SummaryCollapse
            title="Summary"
            className="bg-buttonBg dark:bg-buttonFill hover:bg-buttonHover mt-4 h-full transition-colors"
          >
            <Summary
              account={account}
              amount={finalAmount}
              market={equityModalPortfolio}
            />
          </SummaryCollapse>
        ) : null}

        <Tooltip
          avoidCollisions
          side="top"
          align="center"
          className="z-10"
          disabled={!tooltip}
          content={<Typography size={12}>{tooltip}</Typography>}
        >
          <Button
            size="lg"
            className="mt-4"
            disabled={isDisabled}
            isLoading={isLoading}
            onClick={handleWithdraw}
          >
            {ctaLabel}
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};
